import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getHostProfile } from '../../../services/firestore/hosts';
import { getUserData } from '../../../services/auth';
import { getHostTrips } from '../../../services/firestore/trips';
import { getHostBookings } from '../../../services/firestore/bookings';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import { updateProfile } from 'firebase/auth';
import { uploadImages, uploadImage } from '../../../services/storage';
import { api } from '../../../services/api';
import HostApplicationModal from '../components/HostApplicationModal';
import { ArrowRight, Upload, X, Image as ImageIcon, MapPin, Calendar, Package, Camera, CheckCircle, Clock } from 'lucide-react';

const HostProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Host profile fields
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [propertyName, setPropertyName] = useState('');
  const [propertyLocation, setPropertyLocation] = useState('');
  const [propertyDescription, setPropertyDescription] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [propertyCapacity, setPropertyCapacity] = useState('');
  const [propertyWebsite, setPropertyWebsite] = useState('');
  const [propertyPhotos, setPropertyPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [trips, setTrips] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [userData, setUserData] = useState(null);
  // Profile picture state
  const [photoURL, setPhotoURL] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;
      try {
        const [profile, tripsData, bookingsData, userData] = await Promise.all([
          getHostProfile(user.uid),
          getHostTrips(user.uid).catch(() => []),
          getHostBookings(user.uid).catch(() => []),
          getUserData(user.uid).catch(() => null)
        ]);
        
        if (profile) {
          setBio(profile.bio || '');
          if (profile.property) {
            setPropertyName(profile.property.name || '');
            setPropertyLocation(profile.property.location || '');
            setPropertyDescription(profile.property.description || '');
            setPropertyType(profile.property.type || '');
            setPropertyCapacity(profile.property.capacity?.toString() || '');
            setPropertyWebsite(profile.property.website || '');
            setExistingPhotos(profile.property.photos || []);
          }
        }
        
        // Load profile picture and user data
        if (userData) {
          setPhotoURL(userData.photoURL || user.photoURL || '');
          setDisplayName(userData.displayName || user.displayName || '');
          setUserData(userData);
        } else {
          setPhotoURL(user.photoURL || '');
          setDisplayName(user.displayName || '');
          setUserData(user);
        }
        
        setTrips(tripsData || []);
        setBookings(bookingsData || []);
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [user]);

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const newPreviews = [];
    const newPhotos = [];

    files.forEach((file) => {
      if (file.type.startsWith('image/')) {
        newPhotos.push(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          newPreviews.push(e.target.result);
          if (newPreviews.length === files.length) {
            setPhotoPreviews([...photoPreviews, ...newPreviews]);
            setPropertyPhotos([...propertyPhotos, ...newPhotos]);
          }
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removePhoto = (index) => {
    const newPreviews = [...photoPreviews];
    const newPhotos = [...propertyPhotos];
    newPreviews.splice(index, 1);
    newPhotos.splice(index, 1);
    setPhotoPreviews(newPreviews);
    setPropertyPhotos(newPhotos);
  };

  const removeExistingPhoto = (index) => {
    const newPhotos = [...existingPhotos];
    newPhotos.splice(index, 1);
    setExistingPhotos(newPhotos);
  };

  const handleProfilePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setPhotoFile(file);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const removeProfilePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    setUploadingPhoto(false);

    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      let newPhotoURL = photoURL;

      // Upload profile picture if selected
      if (photoFile) {
        setUploadingPhoto(true);
        try {
          const path = `profiles/${user.uid}/${Date.now()}_${photoFile.name}`;
          newPhotoURL = await uploadImage(photoFile, path);
          setPhotoURL(newPhotoURL);
          setPhotoFile(null);
          setPhotoPreview(null);
        } catch (uploadError) {
          setError('Failed to upload profile picture. Please try again.');
          setUploadingPhoto(false);
          setSaving(false);
          return;
        }
        setUploadingPhoto(false);
      }

      // Upload new property photos if any
      let newPhotoURLs = [];
      if (propertyPhotos.length > 0) {
        newPhotoURLs = await uploadImages(propertyPhotos, `properties/${user.uid}`);
      }

      // Combine existing and new photos
      const allPhotos = [...existingPhotos, ...newPhotoURLs];

      // Create or update host profile via backend API
      await api.createHostProfile(user.uid, bio || propertyDescription || '');

      // Update host document with property details
      await setDoc(
        doc(db, 'hosts', user.uid),
        {
          userId: user.uid,
          bio: bio || propertyDescription || '',
          verified: false,
          payoutsEnabled: false,
          property: {
            name: propertyName,
            location: propertyLocation,
            description: propertyDescription,
            type: propertyType,
            capacity: parseInt(propertyCapacity) || 1,
            website: propertyWebsite,
            photos: allPhotos,
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Update user document with photoURL and displayName
      await setDoc(
        doc(db, 'users', user.uid),
        {
          displayName: displayName || user.displayName,
          photoURL: newPhotoURL,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Update Firebase Auth profile
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: displayName || user.displayName,
          photoURL: newPhotoURL,
        });
      }

      // Redirect back to dashboard
      navigate('/host/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to save profile');
      setSaving(false);
      setUploadingPhoto(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h1 className="text-4xl font-light text-gray-900 mb-2">Host Profile</h1>
              <p className="text-lg text-gray-600 font-light">
                Manage your property details and view your listings and bookings
              </p>
            </div>
            {userData && (
              <div className="flex items-center gap-2">
                {userData.approvedAsHost ? (
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Approved Host
                  </span>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                      <Clock className="w-4 h-4" />
                      Pending Approval
                    </span>
                    <button
                      onClick={() => setShowApplicationModal(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Apply Now
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8 sm:p-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-lg bg-red-50 border-2 border-red-300 p-4">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            )}

            {/* Profile Picture */}
            <div className="flex flex-col items-center mb-6 pb-6 border-b border-gray-200">
              <div className="relative group">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-emerald-100 bg-gray-100">
                  {photoPreview || photoURL ? (
                    <img
                      src={photoPreview || photoURL}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-100 to-green-100">
                      <ImageIcon className="w-16 h-16 text-emerald-600/50" />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 p-3 bg-emerald-600 text-white rounded-full shadow-lg hover:bg-emerald-700 transition-colors group-hover:scale-110 transition-transform"
                  title="Change profile picture"
                >
                  <Camera className="w-5 h-5" />
                </button>
                {photoPreview && (
                  <button
                    type="button"
                    onClick={removeProfilePhoto}
                    className="absolute top-0 right-0 p-2 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 transition-colors"
                    title="Remove photo"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleProfilePhotoSelect}
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Click the camera icon to upload a profile picture
                <br />
                Max size: 5MB
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  id="displayName"
                  type="text"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                  placeholder="John Doe"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {displayName.length.toLocaleString()} characters
                </p>
              </div>

              <div>
                <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
                  About You
                </label>
                <textarea
                  id="bio"
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                  placeholder="Tell guests about yourself..."
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {bio.length.toLocaleString()} characters
                </p>
              </div>

              <div>
                <label htmlFor="propertyName" className="block text-sm font-medium text-gray-700 mb-2">
                  Property Name *
                </label>
                <input
                  id="propertyName"
                  type="text"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                  placeholder="Cozy Mountain Retreat"
                  value={propertyName}
                  onChange={(e) => setPropertyName(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="propertyLocation" className="block text-sm font-medium text-gray-700 mb-2">
                  Location *
                </label>
                <input
                  id="propertyLocation"
                  type="text"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                  placeholder="City, Country"
                  value={propertyLocation}
                  onChange={(e) => setPropertyLocation(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700 mb-2">
                  Property Type *
                </label>
                <select
                  id="propertyType"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                  value={propertyType}
                  onChange={(e) => setPropertyType(e.target.value)}
                >
                  <option value="">Select type</option>
                  <option value="house">House</option>
                  <option value="apartment">Apartment</option>
                  <option value="villa">Villa</option>
                  <option value="cabin">Cabin</option>
                  <option value="cottage">Cottage</option>
                  <option value="retreat">Retreat</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="propertyCapacity" className="block text-sm font-medium text-gray-700 mb-2">
                  Guest Capacity *
                </label>
                <input
                  id="propertyCapacity"
                  type="number"
                  min="1"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                  placeholder="2"
                  value={propertyCapacity}
                  onChange={(e) => setPropertyCapacity(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="propertyWebsite" className="block text-sm font-medium text-gray-700 mb-2">
                  Property Website
                </label>
                <input
                  id="propertyWebsite"
                  type="url"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                  placeholder="https://example.com"
                  value={propertyWebsite}
                  onChange={(e) => setPropertyWebsite(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">Optional: Add your property's website URL</p>
              </div>

              <div>
                <label htmlFor="propertyDescription" className="block text-sm font-medium text-gray-700 mb-2">
                  Property Description *
                </label>
                <textarea
                  id="propertyDescription"
                  rows="4"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                  placeholder="Describe your property, its unique features, and what makes it special..."
                  value={propertyDescription}
                  onChange={(e) => setPropertyDescription(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Property Photos *
                </label>
                <div className="mt-2">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-2 text-gray-500" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG up to 10MB (max 5 photos)</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoSelect}
                      disabled={propertyPhotos.length + existingPhotos.length >= 5}
                    />
                  </label>
                </div>

                {/* Existing Photos */}
                {existingPhotos.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {existingPhotos.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={photo}
                          alt={`Property ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeExistingPhoto(index)}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* New Photo Previews */}
                {photoPreviews.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {photoPreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={`New ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {(existingPhotos.length === 0 && propertyPhotos.length === 0) && (
                  <p className="mt-2 text-xs text-red-600">At least one photo is required</p>
                )}
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/host/dashboard')}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || uploadingPhoto || !displayName || !propertyName || !propertyLocation || !propertyType || !propertyCapacity || !propertyDescription || (existingPhotos.length === 0 && propertyPhotos.length === 0)}
                className="flex-1 group bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-4 rounded-lg text-base font-medium shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:shadow-emerald-300/50 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving || uploadingPhoto ? (
                  uploadingPhoto ? 'Uploading photo...' : 'Saving...'
                ) : (
                  <>
                    Save Profile
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Listings Section */}
        <div className="mt-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8 sm:p-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-light text-gray-900 mb-1 flex items-center gap-2">
                <MapPin className="w-6 h-6" />
                My Listings
              </h2>
              <p className="text-sm text-gray-600">Your trip listings and experiences</p>
            </div>
            <Link
              to="/host/trips/create"
              className="text-emerald-700 hover:text-emerald-800 text-sm font-medium flex items-center gap-1"
            >
              Create New
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {trips.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">You haven't created any listings yet.</p>
              <Link
                to="/host/trips/create"
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors text-sm font-medium"
              >
                Create Your First Listing
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {trips.slice(0, 4).map((trip) => (
                <Link
                  key={trip.id}
                  to={`/host/trips/${trip.id}/edit`}
                  className="block bg-gradient-to-br from-emerald-50/50 to-green-50/30 rounded-lg border border-emerald-100 p-4 hover:shadow-md transition-all hover:border-emerald-200"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-gray-900 mb-1">{trip.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">{trip.location}</p>
                      <p className="text-sm font-medium text-emerald-700">${trip.price}</p>
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        trip.status === 'published'
                          ? 'bg-emerald-100 text-emerald-800'
                          : trip.status === 'draft'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {trip.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {trips.length > 4 && (
            <div className="mt-4 text-center">
              <Link
                to="/host/trips"
                className="text-emerald-700 hover:text-emerald-800 text-sm font-medium"
              >
                View all {trips.length} listings →
              </Link>
            </div>
          )}
        </div>

        {/* Bookings Section */}
        <div className="mt-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8 sm:p-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-light text-gray-900 mb-1 flex items-center gap-2">
                <Calendar className="w-6 h-6" />
                Bookings
              </h2>
              <p className="text-sm text-gray-600">Guest bookings for your listings</p>
            </div>
            <Link
              to="/host/bookings"
              className="text-emerald-700 hover:text-emerald-800 text-sm font-medium flex items-center gap-1"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {bookings.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No bookings yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.slice(0, 4).map((booking) => (
                <Link
                  key={booking.id}
                  to={`/host/bookings/${booking.id}`}
                  className="block bg-gradient-to-br from-emerald-50/50 to-green-50/30 rounded-lg border border-emerald-100 p-4 hover:shadow-md transition-all hover:border-emerald-200"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-gray-900 mb-1">
                        Booking #{booking.id?.substring(0, 8)}
                      </h3>
                      {booking.tripTitle && (
                        <p className="text-sm text-gray-600 mb-2">{booking.tripTitle}</p>
                      )}
                      {booking.totalAmount && (
                        <p className="text-sm font-medium text-emerald-700">
                          ${booking.totalAmount}
                        </p>
                      )}
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        booking.status === 'confirmed'
                          ? 'bg-emerald-100 text-emerald-800'
                          : booking.status === 'pending'
                          ? 'bg-amber-100 text-amber-800'
                          : booking.status === 'canceled'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {booking.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {bookings.length > 4 && (
            <div className="mt-4 text-center">
              <Link
                to="/host/bookings"
                className="text-emerald-700 hover:text-emerald-800 text-sm font-medium"
              >
                View all {bookings.length} bookings →
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Host Application Modal */}
      {user && (
        <HostApplicationModal
          isOpen={showApplicationModal}
          onClose={() => setShowApplicationModal(false)}
          userId={user.uid}
        />
      )}
    </div>
  );
};

export default HostProfile;

